import type { ClientDictionaries } from "../../api/types/clientTypes";
import type {
  Field,
  ObjectFieldValues,
  ObjectWithFieldValuesBase,
  SearchObjectsRequest,
} from "../../api/types/objectTypes";
import { getSelectOptionsFromDictionary } from "../../utilities/dictionariesHelper";
import {
  getColumnDefinitionForField,
  getColumnDeinitionsForTimestampFields,
} from "../common/columns/columnDefinitions";
import type { FilterState } from "../common/filters/filterState";
import { getApiObjectFilters, getVisibleColumnsAndFiltersIds } from "../common/filters/filterState";
import type { TableFilterDefinition } from "../common/filters/filterTypes";
import { isFieldFilterable } from "./helpers";

const getFilterDefinitionForField = <R extends ObjectWithFieldValuesBase>(
  field: Field,
  dictionaries: ClientDictionaries
): TableFilterDefinition<R> => {
  const { id, type, name, configuration } = field;
  const filterDefinition = { id, name, dynamic: true };

  switch (type) {
    case "Number":
    case "Money":
    case "Percent": {
      return {
        ...filterDefinition,
        type: "number",
      };
    }
    case "Checkbox": {
      return {
        ...filterDefinition,
        type: "boolean",
      };
    }
    case "Date": {
      return {
        ...filterDefinition,
        type: "date",
      };
    }
    case "Select":
    case "MultiSelect": {
      const predefinedOptions =
        configuration?.$type === "Select" ? getSelectOptionsFromDictionary(configuration.fieldType, dictionaries) : [];

      return {
        ...filterDefinition,
        type: "multi_select",
        predefinedOptions,
      };
    }
    case "UserDefinedOptionsSelect":
    case "UserDefinedOptionsMultiSelect": {
      const predefinedOptions =
        configuration?.$type === "UserDefinedOptionsSelect" ? configuration.userDefinedOptions : [];

      return {
        ...filterDefinition,
        type: "multi_select",
        predefinedOptions,
      };
    }
    case "Lookup": {
      return {
        ...filterDefinition,
        type: "multi_select",
        asyncOptions: true,
      };
    }
    default: {
      return {
        ...filterDefinition,
        type: "text",
      };
    }
  }
};

export const getObjectFilterDefinitions = (
  allFields: Field[],
  defaultGridFieldIds: string[],
  showTimestampsByDefault: boolean | undefined,
  dictionaries: ClientDictionaries
) => {
  const filterDefinitions = allFields
    .filter(isFieldFilterable)
    .map((field) => getFilterDefinitionForField(field, dictionaries));

  const columnDefinitions = allFields.map((field) =>
    getColumnDefinitionForField(field, defaultGridFieldIds.includes(field.id))
  );

  columnDefinitions.push(...getColumnDeinitionsForTimestampFields(showTimestampsByDefault));

  return { filterDefinitions, columnDefinitions };
};

export const getSearchObjectsFilterPayload = (
  filterState: FilterState<ObjectFieldValues>
): Omit<SearchObjectsRequest, "range"> => {
  const { filters, searchTerm, sort } = getApiObjectFilters(filterState);
  const fieldIds = getVisibleColumnsAndFiltersIds(filterState);

  return {
    fieldIds,
    includeLookupObjects: true,
    filters,
    searchTerm,
    sort,
  };
};
